import { LoanPurpose } from './generated/graphql-types'

export enum QuestionSetSlug {
  AmazonBenefits = 'amazon-benefits',
  ApplyPurchase = 'apply-purchase',
  ApplyRefinance = 'apply-refinance',
  ApplyHomeEquity = 'apply-he',
  Compare = 'compare-ntl',
  CreditConsent = 'credit-consent',
  FullPurchase = 'full-purchase',
  FullRefinance = 'full-refinance',
  NTL = 'ntl',
  PhoneApp = 'phone-app',
  Plaid = 'plaid',
  PrePricingPurchase = 'pre-pricing-purchase',
  PrePricingRefinance = 'pre-pricing-refinance',
  PrePricingHomeEquity = 'pre-pricing-he',
  PurchaseFastPass = 'purchase-fastpass',
  RhApplyPurchase = 'rh-apply-purchase',
  RhApplyRefinance = 'rh-apply-refinance',
  RhPrePricingPurchase = 'rh-pre-pricing-purchase',
  RhPrePricingRefinance = 'rh-pre-pricing-refinance',
  SimpleV1 = 'simple-v1',
  SimpleV1Results = 'simple-v1-results',
}

export enum SplitFlowSegments {
  PrePricing = 'prePricing',
  Apply = 'apply',
}

type SplitMapType = Record<
  LoanPurpose,
  Record<SplitFlowSegments, QuestionSetSlug>
>

export const splitFlowSlugMap: SplitMapType = {
  [LoanPurpose.Refinance]: {
    [SplitFlowSegments.PrePricing]: QuestionSetSlug.PrePricingRefinance,
    [SplitFlowSegments.Apply]: QuestionSetSlug.ApplyRefinance,
  },
  [LoanPurpose.Purchase]: {
    [SplitFlowSegments.PrePricing]: QuestionSetSlug.PrePricingPurchase,
    [SplitFlowSegments.Apply]: QuestionSetSlug.ApplyPurchase,
  },
  [LoanPurpose.HomeEquity]: {
    [SplitFlowSegments.PrePricing]: QuestionSetSlug.PrePricingHomeEquity,
    [SplitFlowSegments.Apply]: QuestionSetSlug.ApplyHomeEquity,
  },
}

/**
 * Get the question set slug based on the loan purpose and the status of the first flow (pre-pricing)
 * pre-pricing-purchase -> apply-purchase
 * pre-pricing-refinance -> apply-refinance
 *
 * Special cases for Robinhood Gold Members
 * rh-pre-pricing-purchase -> rh-apply-purchase
 * rh-pre-pricing-refinance -> rh-apply-refinance
 */
export const getQuestionSetSlug = ({
  isPrePricingIncomplete,
  loanPurpose,
  isRobinhoodGoldMember = false,
  defaultSlug = QuestionSetSlug.PrePricingPurchase,
}: {
  isPrePricingIncomplete: boolean
  loanPurpose: LoanPurpose
  isRobinhoodGoldMember?: boolean
  defaultSlug?: QuestionSetSlug
}) => {
  const segment = isPrePricingIncomplete
    ? SplitFlowSegments.PrePricing
    : SplitFlowSegments.Apply

  const flowMap = {
    [LoanPurpose.Purchase]: {
      [SplitFlowSegments.PrePricing]: isRobinhoodGoldMember
        ? QuestionSetSlug.RhPrePricingPurchase
        : QuestionSetSlug.PrePricingPurchase,
      [SplitFlowSegments.Apply]: isRobinhoodGoldMember
        ? QuestionSetSlug.RhApplyPurchase
        : QuestionSetSlug.ApplyPurchase,
    },
    [LoanPurpose.Refinance]: {
      [SplitFlowSegments.PrePricing]: isRobinhoodGoldMember
        ? QuestionSetSlug.RhPrePricingRefinance
        : QuestionSetSlug.PrePricingRefinance,
      [SplitFlowSegments.Apply]: isRobinhoodGoldMember
        ? QuestionSetSlug.RhApplyRefinance
        : QuestionSetSlug.ApplyRefinance,
    },
    [LoanPurpose.HomeEquity]: {
      [SplitFlowSegments.PrePricing]: QuestionSetSlug.PrePricingHomeEquity,
      [SplitFlowSegments.Apply]: QuestionSetSlug.ApplyHomeEquity,
    },
  }

  return flowMap[loanPurpose]?.[segment] ?? defaultSlug
}
